import { useAuth } from "@/context/AuthContext";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trophy, Check, X } from "lucide-react";

// Bot-detected win/loss outcomes surfaced for one-tap human approval (never auto-applied).
export function OutcomeReview({ onChange }) {
  const { user: _me } = useAuth();
  const _connectorMode = !!_me?.features?.assistant_claude;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/email/outcome-suggestions").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const act = async (it, action) => {
    setBusy(it.event_id + it.bid_id + action);
    try {
      const { data } = await api.post(`/email/events/${it.event_id}/outcome`, { action, bid_id: it.bid_id });
      toast.success(action === "reject" ? "Dismissed" : `Recorded ${data.outcome} on ${it.bid_name}`);
      setItems((p) => p.filter((x) => (action === "reject" ? x.event_id !== it.event_id : !(x.event_id === it.event_id && x.bid_id === it.bid_id))));
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  if (items.length === 0) return null;

  if (_connectorMode) return null;
  return (
    <>
      <button data-testid="outcome-review-banner" onClick={() => setOpen(true)}
        className="w-full mb-4 flex items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-left hover:bg-emerald-100 transition">
        <span className="grid place-items-center w-7 h-7 rounded-full bg-emerald-600 text-white shrink-0"><Trophy className="w-4 h-4" /></span>
        <span className="flex-1 text-sm text-emerald-900">
          <span className="font-semibold">Flagged from email: {items.length} project outcome{items.length === 1 ? "" : "s"} from your email</span> — approve to record won/lost on that one project.
        </span>
        <span className="text-xs font-semibold text-emerald-700">Review →</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="outcome-review-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detected outcomes — your call</DialogTitle>
            <DialogDescription>These were flagged from email. Each row is a single project — approving records the outcome on that one bid only, never the whole client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">All caught up.</div>}
            {items.map((it) => (
              <Card key={it.event_id + it.bid_id} data-testid={`outcome-item-${it.event_id}-${it.bid_id}`} className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold uppercase tracking-wide rounded px-2 py-0.5 ${it.decision === "won" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>Sol thinks {it.decision}</span>
                  <span className="font-semibold text-black truncate">{it.bid_name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 truncate">{it.client_name || it.counterparty}{it.summary ? ` · “${it.summary}”` : ""}</div>
                {it.reason && <div className="text-[11px] text-slate-600 mt-1" data-testid={`outcome-reason-${it.event_id}-${it.bid_id}`}><span className="text-slate-400">{it.decision === "lost" ? "Loss reason" : "Reason"}:</span> {it.reason}</div>}
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" data-testid={`outcome-approve-${it.event_id}-${it.bid_id}`} disabled={busy} onClick={() => act(it, "approve")}
                    className={`h-8 text-white ${it.decision === "won" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Record {it.decision}
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`outcome-reject-${it.event_id}-${it.bid_id}`} disabled={busy} onClick={() => act(it, "reject")} className="h-8">
                    <X className="w-3.5 h-3.5 mr-1" /> Not this one
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
